import { useState, useMemo, Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library, type IconName } from '@fortawesome/fontawesome-svg-core';
import { faUserLock, faKey, faPlug, faGlobe, faCube } from '@fortawesome/free-solid-svg-icons';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { CONFIG } from '../../../config';
import {
  useConnectorTemplates,
  useConnectorInstances,
  useCreateConnectorInstance,
  useUpdateConnectorInstance,
  useDeleteConnectorInstance,
  useSetCredential,
  useDeleteCredential,
  useTestConnection,
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useWebhookSubscriptions,
  useCreateWebhookSubscription,
  useUpdateWebhookSubscription,
  useDeleteWebhookSubscription,
  useWebhookDeliveries,
  useRetryDelivery,
  useIntegrationAuditLogs,
  useConnectorInstanceLogs,
  useInitiateOAuth,
  useFieldMappings,
  useMappingRecords,
  useBulkSaveMappings,
  useUpdateProcessingConfig,
  useRecaptureSample,
  useProcessingLogs,
  useLookupCaseTypes,
  useLookupStages,
  useLookupFieldDefinitions,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type {
  ConnectorTemplate,
  ConnectorInstance,
  WebhookEndpoint,
  WebhookSubscription,
  WebhookDelivery,
  IntegrationAuditEntry,
  WebhookFieldMapping,
  WebhookMappingRecord,
  WebhookProcessingLog,
  ProcessingFieldNote,
  CaseTypeLookup,
  StageLookup,
  FieldDefinition,
} from '../../../types/admin';
import css from '../AdminPage.module.css';

// Register FA icons used by connector templates
library.add(faUserLock, faKey, faPlug, faGlobe, faCube, faMicrosoft);

/** Maps FA class names to [prefix, iconName] tuples. Brands icons need 'fab'. */
const BRAND_ICONS = new Set([
  'microsoft',
  'google',
  'apple',
  'github',
  'slack',
  'dropbox',
  'aws',
  'docker',
]);

function ConnectorIcon({ icon }: { icon?: string | null }) {
  if (!icon) return <span>🔌</span>;
  if (!icon.startsWith('fa-')) return <span>{icon}</span>;
  const name = icon.replace(/^fa-/, '') as IconName;
  const prefix = BRAND_ICONS.has(name) ? 'fab' : 'fas';
  try {
    return <FontAwesomeIcon icon={[prefix, name]} size="2x" />;
  } catch {
    return <span>🔌</span>;
  }
}

type SubTab = 'connectors' | 'webhooks' | 'mapping' | 'deliveries' | 'audit';

type ApiErrorShape = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

type ConnectorSchemaProperty = {
  title?: string;
  description?: string;
  default?: unknown;
};

type ConnectorSchema = {
  properties?: Record<string, ConnectorSchemaProperty>;
  required?: string[];
};

type IntegrationAuditDetails = Record<string, unknown> & {
  success?: boolean;
  status?: string;
  method?: string;
  url?: string;
  latency_ms?: number;
  error?: string;
};

function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybeError = error as ApiErrorShape;
  return maybeError.response?.data?.error || fallback;
}

function getConnectorSchema(template: ConnectorTemplate | null): ConnectorSchema {
  if (!template || typeof template.config_schema !== 'object' || template.config_schema == null) {
    return {};
  }
  return template.config_schema as ConnectorSchema;
}

function toDisplayString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function getFieldName(fd: FieldDefinition): string {
  const legacy = (fd as unknown as { field_name?: string }).field_name;
  return (fd.name || legacy || '').toLowerCase();
}

function getFieldKey(fd: FieldDefinition): string {
  const legacy = (fd as unknown as { field_key?: string }).field_key;
  return (fd.key || legacy || '').toLowerCase();
}

function getFieldLabel(fd: FieldDefinition): string {
  const legacy = (fd as unknown as { field_name?: string }).field_name;
  return fd.name || legacy || `Field #${fd.id}`;
}

/* ────────────────────────────────────────── */

export default function Integrations() {
  const [subTab, setSubTab] = useState<SubTab>('connectors');

  return (
    <div className={css.section}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className={css.sectionTitle}>Integrations</h2>
        <a
          href={`${CONFIG.BACKEND_BASE_URL}/docs/integrations-api`}
          target="_blank"
          rel="noopener noreferrer"
          className={css.btnSmall}
          style={{ textDecoration: 'none' }}
        >
          📖 API Docs
        </a>
      </div>

      <div className={css.tabs}>
        {(['connectors', 'webhooks', 'mapping', 'deliveries', 'audit'] as SubTab[]).map((t) => (
          <button
            key={t}
            className={`${css.tab} ${subTab === t ? css.tabActive : ''}`}
            onClick={() => setSubTab(t)}
          >
            {t === 'connectors'
              ? 'Connectors'
              : t === 'webhooks'
                ? 'Webhooks'
                : t === 'mapping'
                  ? 'ETL Mapping'
                  : t === 'deliveries'
                    ? 'Deliveries'
                    : 'Audit Log'}
          </button>
        ))}
      </div>

      {subTab === 'connectors' && <ConnectorsPanel />}
      {subTab === 'webhooks' && <WebhooksPanel />}
      {subTab === 'mapping' && <MappingPanel />}
      {subTab === 'deliveries' && <DeliveriesPanel />}
      {subTab === 'audit' && <AuditPanel />}
    </div>
  );
}

/* ══════════════════════════════════════════
 *  CONNECTORS PANEL
 * ══════════════════════════════════════════ */

function ConnectorsPanel() {
  const { showToast } = useToast();
  const { data: templates = [], isLoading: tplLoading } = useConnectorTemplates();
  const { data: instances = [], isLoading: instLoading } = useConnectorInstances();
  const createInstance = useCreateConnectorInstance();
  const updateInstance = useUpdateConnectorInstance();
  const deleteInstance = useDeleteConnectorInstance();
  const setCredential = useSetCredential();
  const deleteCredential = useDeleteCredential();
  const testConnection = useTestConnection();
  const initiateOAuth = useInitiateOAuth();

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editInstance, setEditInstance] = useState<ConnectorInstance | null>(null);
  const [credModal, setCredModal] = useState<{
    instanceId: number;
    instanceName: string;
    authType: string;
  } | null>(null);
  const [testResult, setTestResult] = useState<{
    instanceId: number;
    result: string;
    ok: boolean;
  } | null>(null);
  const [logsModal, setLogsModal] = useState<{ instanceId: number; instanceName: string } | null>(
    null,
  );

  // Create form state
  const [createForm, setCreateForm] = useState<{
    template_id: number;
    name: string;
    config: Record<string, string>;
  }>({ template_id: 0, name: '', config: {} });
  // Credential form state
  const [credForm, setCredForm] = useState({ field_name: '', value: '' });

  // Derive the selected template's config schema properties for dynamic field rendering
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === createForm.template_id) ?? null,
    [templates, createForm.template_id],
  );
  const schemaProperties = useMemo(() => {
    const props = getConnectorSchema(selectedTemplate).properties ?? {};
    return Object.entries(props) as [string, ConnectorSchemaProperty][];
  }, [selectedTemplate]);
  const requiredFields: string[] = useMemo(
    () => getConnectorSchema(selectedTemplate).required ?? [],
    [selectedTemplate],
  );

  const filtered = useMemo(
    () => instances.filter((i) => i.name.toLowerCase().includes(search.toLowerCase())),
    [instances, search],
  );

  const loading = tplLoading || instLoading;

  async function handleCreate() {
    if (!createForm.template_id || !createForm.name.trim()) {
      showToast('Template and name are required', 'error');
      return;
    }
    // Strip empty strings so optional fields aren't passed as ''
    const config = Object.fromEntries(
      Object.entries(createForm.config).filter(([, v]) => v.trim() !== ''),
    );
    try {
      await createInstance.mutateAsync({
        template_id: createForm.template_id,
        name: createForm.name,
        config,
      });
      showToast('Instance created', 'success');
      setShowCreate(false);
      setCreateForm({ template_id: 0, name: '', config: {} });
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to create instance'), 'error');
    }
  }

  async function handleUpdate() {
    if (!editInstance) return;
    try {
      await updateInstance.mutateAsync({
        id: editInstance.id,
        name: editInstance.name,
        status: editInstance.status,
        config: editInstance.config,
      });
      showToast('Instance updated', 'success');
      setEditInstance(null);
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to update'), 'error');
    }
  }

  async function handleDelete(id: number, name: string) {
    if (!window.confirm(`Delete connector "${name}"? This cannot be undone.`)) return;
    try {
      await deleteInstance.mutateAsync(id);
      showToast('Instance deleted', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to delete'), 'error');
    }
  }

  async function handleSetCredential() {
    if (!credModal) return;
    if (!credForm.field_name.trim() || !credForm.value.trim()) {
      showToast('Field name and value are required', 'error');
      return;
    }
    try {
      await setCredential.mutateAsync({
        instanceId: credModal.instanceId,
        fieldName: credForm.field_name,
        value: credForm.value,
      });
      showToast('Credential saved (encrypted)', 'success');
      setCredForm({ field_name: '', value: '' });
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to save credential'), 'error');
    }
  }

  async function handleDeleteCredential(instanceId: number, fieldName: string) {
    if (!window.confirm(`Remove credential "${fieldName}"?`)) return;
    try {
      await deleteCredential.mutateAsync({ instanceId, fieldName });
      showToast('Credential removed', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to remove credential'), 'error');
    }
  }

  async function handleTestConnection(instanceId: number) {
    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync(instanceId);
      setTestResult({
        instanceId,
        ok: result.success,
        result: result.success
          ? `Connected (${result.latency_ms}ms)`
          : `Failed: ${result.error || 'Unknown error'}`,
      });
    } catch (e: unknown) {
      setTestResult({
        instanceId,
        ok: false,
        result: getApiErrorMessage(e, 'Connection test failed'),
      });
    }
  }

  if (loading)
    return (
      <div className={css.emptyState}>
        <div className={css.spinner} />
      </div>
    );

  return (
    <>
      {/* Templates overview */}
      <h3 className={css.sectionSubtitle}>Connector Templates</h3>
      {templates.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyMessage}>No templates configured</span>
        </div>
      ) : (
        <div className={css.statsGrid}>
          {templates.map((t: ConnectorTemplate) => (
            <div key={t.id} className={css.statCard}>
              <div className={css.statValue}>
                <ConnectorIcon icon={t.icon} />
              </div>
              <div className={css.statLabel}>{t.name}</div>
              <span className={t.is_active ? css.badgeSuccess : css.badgeNeutral}>
                {t.is_active ? 'Active' : 'Inactive'}
              </span>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>{t.auth_type}</div>
            </div>
          ))}
        </div>
      )}

      {/* Instances */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
        }}
      >
        <h3 className={css.sectionSubtitle} style={{ margin: 0 }}>
          Connector Instances
        </h3>
        <button className={css.btnPrimary} onClick={() => setShowCreate(true)}>
          + New Instance
        </button>
      </div>

      <div className={css.toolbar}>
        <div className={css.searchWrapper}>
          <span className={css.searchIcon}>🔍</span>
          <input
            className={css.searchInput}
            placeholder="Search instances…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyIcon}>📡</span>
          <span className={css.emptyMessage}>No connector instances yet</span>
        </div>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Template</th>
              <th>Auth</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inst) => (
              <tr key={inst.id}>
                <td>{inst.name}</td>
                <td>{inst.template_name}</td>
                <td>{inst.auth_type}</td>
                <td>
                  <span
                    className={
                      inst.status === 'active'
                        ? css.badgeSuccess
                        : inst.status === 'paused'
                          ? css.badgeWarning
                          : css.badgeError
                    }
                  >
                    {inst.status}
                  </span>
                </td>
                <td>{new Date(inst.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => setEditInstance({ ...inst })}
                    >
                      Edit
                    </button>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() =>
                        setCredModal({
                          instanceId: inst.id,
                          instanceName: inst.name,
                          authType: inst.auth_type,
                        })
                      }
                    >
                      🔑
                    </button>
                    {inst.auth_type === 'oauth2' && (
                      <button
                        className={`${css.btnSecondary} ${css.btnSmall}`}
                        disabled={initiateOAuth.isPending}
                        title="Start OAuth2 authorisation flow"
                        onClick={async () => {
                          try {
                            const { authorize_url } = await initiateOAuth.mutateAsync(inst.id);
                            window.open(authorize_url, '_blank', 'noopener,noreferrer');
                          } catch {
                            showToast('Failed to generate OAuth2 authorization URL', 'error');
                          }
                        }}
                      >
                        🔗 Authorise
                      </button>
                    )}
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => handleTestConnection(inst.id)}
                      disabled={testConnection.isPending}
                    >
                      {testConnection.isPending && testResult === null ? '⏳' : '🧪'}
                    </button>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => setLogsModal({ instanceId: inst.id, instanceName: inst.name })}
                    >
                      Logs
                    </button>
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={() => handleDelete(inst.id, inst.name)}
                    >
                      Del
                    </button>
                  </div>
                  {testResult && testResult.instanceId === inst.id && (
                    <span
                      style={{ marginLeft: 8 }}
                      className={testResult.ok ? css.badgeSuccess : css.badgeError}
                    >
                      {testResult.result}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create instance modal */}
      {showCreate && (
        <div className={css.modalBackdrop} onClick={() => setShowCreate(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>New Connector Instance</span>
              <button className={css.modalClose} onClick={() => setShowCreate(false)}>
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Template</label>
                <select
                  className={css.formSelect}
                  value={createForm.template_id}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      template_id: Number(e.target.value),
                      config: {},
                    }))
                  }
                >
                  <option value={0}>Select a template…</option>
                  {templates
                    .filter((t) => t.is_active)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.auth_type})
                      </option>
                    ))}
                </select>
                {selectedTemplate?.description && (
                  <span className={css.formHint}>{selectedTemplate.description}</span>
                )}
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Instance Name</label>
                <input
                  className={css.formInput}
                  placeholder="e.g. Production Dropbox"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              {schemaProperties.length > 0 && (
                <>
                  <h4
                    style={{
                      margin: '12px 0 8px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-300)',
                    }}
                  >
                    Configuration
                  </h4>
                  {schemaProperties.map(([key, prop]) => (
                    <div className={css.formGroup} key={key}>
                      <label className={css.formLabel}>
                        {prop.title || key}
                        {requiredFields.includes(key) ? ' *' : ''}
                      </label>
                      <input
                        className={css.formInput}
                        type={
                          key.toLowerCase().includes('secret') ||
                          key.toLowerCase().includes('password')
                            ? 'password'
                            : 'text'
                        }
                        placeholder={prop.default ? String(prop.default) : prop.description || ''}
                        value={createForm.config[key] ?? ''}
                        onChange={(e) =>
                          setCreateForm((f) => ({
                            ...f,
                            config: { ...f.config, [key]: e.target.value },
                          }))
                        }
                      />
                      {prop.description && <span className={css.formHint}>{prop.description}</span>}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleCreate}
                disabled={createInstance.isPending}
              >
                {createInstance.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit instance modal */}
      {editInstance && (
        <div className={css.modalBackdrop} onClick={() => setEditInstance(null)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>Edit Instance</span>
              <button className={css.modalClose} onClick={() => setEditInstance(null)}>
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Name</label>
                <input
                  className={css.formInput}
                  value={editInstance.name}
                  onChange={(e) =>
                    setEditInstance((prev) => (prev ? { ...prev, name: e.target.value } : null))
                  }
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Status</label>
                <select
                  className={css.formSelect}
                  value={editInstance.status}
                  onChange={(e) =>
                    setEditInstance((prev) =>
                      prev
                        ? { ...prev, status: e.target.value as ConnectorInstance['status'] }
                        : null,
                    )
                  }
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
              {(() => {
                const tpl = templates.find((t) => t.id === editInstance.template_id) ?? null;
                const props = Object.entries(getConnectorSchema(tpl).properties ?? {}) as [
                  string,
                  ConnectorSchemaProperty,
                ][];
                if (!props.length) return null;
                return (
                  <>
                    <h4
                      style={{
                        margin: '12px 0 8px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Configuration
                    </h4>
                    {props.map(([key, prop]) => (
                      <div className={css.formGroup} key={key}>
                        <label className={css.formLabel}>{prop.title || key}</label>
                        <input
                          className={css.formInput}
                          type={
                            key.toLowerCase().includes('secret') ||
                            key.toLowerCase().includes('password')
                              ? 'password'
                              : 'text'
                          }
                          placeholder={prop.default ? String(prop.default) : prop.description || ''}
                          value={(editInstance.config[key] as string) ?? ''}
                          onChange={(e) =>
                            setEditInstance((prev) =>
                              prev
                                ? { ...prev, config: { ...prev.config, [key]: e.target.value } }
                                : null,
                            )
                          }
                        />
                        {prop.description && (
                          <span className={css.formHint}>{prop.description}</span>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditInstance(null)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleUpdate}
                disabled={updateInstance.isPending}
              >
                {updateInstance.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credential modal */}
      {credModal && (
        <div
          className={css.modalBackdrop}
          onClick={() => {
            setCredModal(null);
            setCredForm({ field_name: '', value: '' });
          }}
        >
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>Credentials — {credModal.instanceName}</span>
              <button
                className={css.modalClose}
                onClick={() => {
                  setCredModal(null);
                  setCredForm({ field_name: '', value: '' });
                }}
              >
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              {/* Existing credentials */}
              {(instances.find((i) => i.id === credModal.instanceId)?.credentials ?? []).length >
                0 && (
                <table className={css.table} style={{ marginBottom: 16 }}>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Last Rotated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(instances.find((i) => i.id === credModal.instanceId)?.credentials ?? []).map(
                      (c) => (
                        <tr key={c.field_name}>
                          <td>{c.field_name}</td>
                          <td>
                            {c.last_rotated_at
                              ? new Date(c.last_rotated_at).toLocaleDateString()
                              : '—'}
                          </td>
                          <td>
                            <button
                              className={`${css.btnSmall} ${css.btnDanger}`}
                              onClick={() =>
                                handleDeleteCredential(credModal.instanceId, c.field_name)
                              }
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}

              <h4 style={{ margin: '0 0 8px' }}>Add / Rotate Credential</h4>

              {/* Guided quick-add buttons for OAuth2 */}
              {credModal.authType === 'oauth2' &&
                (() => {
                  const existing = new Set(
                    (instances.find((i) => i.id === credModal.instanceId)?.credentials ?? []).map(
                      (c) => c.field_name,
                    ),
                  );
                  const oauthFields = [
                    { name: 'client_id', label: 'Client ID', secret: false },
                    { name: 'client_secret', label: 'Client Secret', secret: true },
                    { name: 'refresh_token', label: 'Refresh Token', secret: true },
                    { name: 'access_token', label: 'Access Token', secret: true },
                  ];
                  const missing = oauthFields.filter((f) => !existing.has(f.name));
                  return missing.length > 0 ? (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 6 }}>
                        Quick add required OAuth2 credentials:
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {missing.map((f) => (
                          <button
                            key={f.name}
                            className={`${css.btnSecondary} ${css.btnSmall}`}
                            onClick={() => setCredForm({ field_name: f.name, value: '' })}
                            style={{ fontSize: '0.75rem' }}
                          >
                            + {f.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Field Name</label>
                  <input
                    className={css.formInput}
                    placeholder={
                      credModal.authType === 'oauth2' ? 'e.g. client_id' : 'e.g. api_key'
                    }
                    value={credForm.field_name}
                    onChange={(e) => setCredForm((f) => ({ ...f, field_name: e.target.value }))}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Value</label>
                  <input
                    className={css.formInput}
                    type="password"
                    placeholder="••••••••"
                    value={credForm.value}
                    onChange={(e) => setCredForm((f) => ({ ...f, value: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button
                className={css.btnSecondary}
                onClick={() => {
                  setCredModal(null);
                  setCredForm({ field_name: '', value: '' });
                }}
              >
                Close
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleSetCredential}
                disabled={setCredential.isPending}
              >
                {setCredential.isPending ? 'Saving…' : 'Save Credential'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connector call logs modal */}
      {logsModal && (
        <ConnectorLogsModal
          instanceId={logsModal.instanceId}
          instanceName={logsModal.instanceName}
          onClose={() => setLogsModal(null)}
        />
      )}
    </>
  );
}

function ConnectorLogsModal({
  instanceId,
  instanceName,
  onClose,
}: {
  instanceId: number;
  instanceName: string;
  onClose: () => void;
}) {
  const { data: logs = [], isLoading } = useConnectorInstanceLogs(instanceId, true);
  return (
    <div className={css.modalBackdrop} onClick={onClose}>
      <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className={css.modalHeader}>
          <span className={css.modalTitle}>Call Logs — {instanceName}</span>
          <button className={css.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={css.modalBody} style={{ maxHeight: 520, overflowY: 'auto' }}>
          {isLoading && <p style={{ color: 'var(--gray-400)' }}>Loading…</p>}
          {!isLoading && logs.length === 0 && (
            <p style={{ color: 'var(--gray-400)' }}>
              No calls recorded yet. Trigger an automation to see logs here.
            </p>
          )}
          {logs.map((entry) => {
            const d = entry.details as IntegrationAuditDetails;
            const ok = d.success !== false && entry.action !== 'request_failed';
            return (
              <div
                key={entry.id}
                style={{
                  borderLeft: `3px solid ${ok ? 'var(--green-500, #22c55e)' : 'var(--red-500, #ef4444)'}`,
                  paddingLeft: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    <span className={ok ? css.badgeSuccess : css.badgeError}>
                      {toDisplayString(d.status, ok ? 'OK' : 'ERR')}
                    </span>{' '}
                    {toDisplayString(d.method, 'UNKNOWN')} {toDisplayString(d.url, '')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    {typeof d.latency_ms === 'number' ? `${d.latency_ms}ms · ` : ''}
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
                {typeof d.error === 'string' && d.error.length > 0 && (
                  <div
                    style={{ fontSize: '0.78rem', color: 'var(--red-400, #f87171)', marginTop: 2 }}
                  >
                    {d.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className={css.modalFooter}>
          <button className={css.btnSecondary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
 *  WEBHOOKS PANEL
 * ══════════════════════════════════════════ */

const ALL_EVENT_TYPES: { group: string; types: { value: string; label: string }[] }[] = [
  {
    group: 'Case',
    types: [
      { value: 'case.created', label: 'case.created — new case opened' },
      { value: 'case.updated', label: 'case.updated — fields modified' },
      { value: 'case.stage_changed', label: 'case.stage_changed — stage transition' },
      { value: 'case.deleted', label: 'case.deleted — case removed' },
    ],
  },
  {
    group: 'Contact',
    types: [
      { value: 'contact.created', label: 'contact.created' },
      { value: 'contact.updated', label: 'contact.updated' },
    ],
  },
  {
    group: 'Task',
    types: [
      { value: 'task.created', label: 'task.created' },
      { value: 'task.completed', label: 'task.completed' },
      { value: 'task.updated', label: 'task.updated' },
    ],
  },
  {
    group: 'Document',
    types: [
      { value: 'document.uploaded', label: 'document.uploaded' },
      { value: 'document.generated', label: 'document.generated' },
      { value: 'document.deleted', label: 'document.deleted' },
    ],
  },
  {
    group: 'Note',
    types: [{ value: 'note.created', label: 'note.created' }],
  },
  {
    group: 'Integration',
    types: [
      { value: 'integration.connected', label: 'integration.connected' },
      { value: 'integration.disconnected', label: 'integration.disconnected' },
      { value: 'integration.error', label: 'integration.error' },
    ],
  },
];

function EventTypeCheckboxes({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
      {ALL_EVENT_TYPES.map(({ group, types }) => (
        <div key={group}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--gray-400)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 4,
            }}
          >
            {group}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {types.map(({ value, label }) => (
              <label
                key={value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggle(value)}
                  style={{ cursor: 'pointer' }}
                />
                <code style={{ fontSize: 'inherit' }}>{label}</code>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function WebhooksPanel() {
  const { showToast } = useToast();
  const { data: endpoints = [], isLoading: epLoading } = useWebhookEndpoints();
  const { data: subscriptions = [], isLoading: subLoading } = useWebhookSubscriptions();
  const { data: instances = [] } = useConnectorInstances();
  const createEndpoint = useCreateWebhookEndpoint();
  const updateEndpoint = useUpdateWebhookEndpoint();
  const deleteEndpoint = useDeleteWebhookEndpoint();
  const createSubscription = useCreateWebhookSubscription();
  const updateSubscription = useUpdateWebhookSubscription();
  const deleteSubscription = useDeleteWebhookSubscription();

  const [showEpCreate, setShowEpCreate] = useState(false);
  const [showSubCreate, setShowSubCreate] = useState(false);
  const [epForm, setEpForm] = useState<{
    instance_id: number;
    event_type_filter: string[];
    endpoint_mode: 'form_input' | 'trigger_update';
  }>({ instance_id: 0, event_type_filter: [], endpoint_mode: 'form_input' });
  const [subForm, setSubForm] = useState<{
    instance_id: number;
    name: string;
    target_url: string;
    event_types: string[];
  }>({ instance_id: 0, name: '', target_url: '', event_types: [] });

  const loading = epLoading || subLoading;

  async function handleCreateEndpoint() {
    try {
      const result = await createEndpoint.mutateAsync({
        instance_id: epForm.instance_id || undefined,
        event_type_filter: epForm.event_type_filter.length ? epForm.event_type_filter : undefined,
        endpoint_mode: epForm.endpoint_mode,
      });
      const data = result?.data;
      const url = data?.inbound_url || '';
      const secret = data?.signing_secret || '';

      // Build a summary for the user to copy
      let msg = `Inbound URL (paste into external app):\n${url}`;
      if (secret) {
        msg += `\n\nSigning Secret (shown ONCE — save it now):\n${secret}`;
      }
      window.prompt('Endpoint created! Copy the details below:', msg);
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          /* prompt fallback is enough */
        }
        showToast('Endpoint created — inbound URL copied to clipboard', 'success');
      } else {
        showToast('Endpoint created', 'success');
      }
      setShowEpCreate(false);
      setEpForm({ instance_id: 0, event_type_filter: [], endpoint_mode: 'form_input' });
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to create endpoint'), 'error');
    }
  }

  async function handleDeleteEndpoint(id: number) {
    if (!window.confirm('Delete this webhook endpoint?')) return;
    try {
      await deleteEndpoint.mutateAsync(id);
      showToast('Endpoint deleted', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to delete'), 'error');
    }
  }

  async function handleToggleEndpoint(ep: WebhookEndpoint) {
    try {
      await updateEndpoint.mutateAsync({ id: ep.id, is_active: !ep.is_active });
      showToast(`Endpoint ${ep.is_active ? 'disabled' : 'enabled'}`, 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to update'), 'error');
    }
  }

  async function handleCreateSubscription() {
    if (!subForm.target_url.trim()) {
      showToast('Target URL is required', 'error');
      return;
    }
    try {
      const result = await createSubscription.mutateAsync({
        instance_id: subForm.instance_id || undefined,
        name: subForm.name || undefined,
        target_url: subForm.target_url,
        event_types: subForm.event_types,
      });
      const signingSecret = result?.data?.signing_secret;
      if (signingSecret) {
        showToast('Subscription created — signing secret shown ONCE, copy it now!', 'warning');
        window.prompt('Copy this signing secret (shown only once):', signingSecret);
      } else {
        showToast('Subscription created', 'success');
      }
      setShowSubCreate(false);
      setSubForm({ instance_id: 0, name: '', target_url: '', event_types: [] });
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to create subscription'), 'error');
    }
  }

  async function handleToggleSubscription(sub: WebhookSubscription) {
    try {
      await updateSubscription.mutateAsync({ id: sub.id, is_active: !sub.is_active });
      showToast(`Subscription ${sub.is_active ? 'disabled' : 'enabled'}`, 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to update'), 'error');
    }
  }

  async function handleDeleteSubscription(id: number) {
    if (!window.confirm('Delete this webhook subscription?')) return;
    try {
      await deleteSubscription.mutateAsync(id);
      showToast('Subscription deleted', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to delete'), 'error');
    }
  }

  if (loading)
    return (
      <div className={css.emptyState}>
        <div className={css.spinner} />
      </div>
    );

  return (
    <>
      {/* ── Inbound Endpoints ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 className={css.sectionSubtitle}>Inbound Endpoints</h3>
        <button className={css.btnPrimary} onClick={() => setShowEpCreate(true)}>
          + New Endpoint
        </button>
      </div>

      {endpoints.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyMessage}>No inbound endpoints</span>
        </div>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Inbound URL</th>
              <th>Mode</th>
              <th>Instance</th>
              <th>Sig Header</th>
              <th>Event Filter</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep: WebhookEndpoint) => (
              <tr key={ep.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ep.endpoint_key}</td>
                <td>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    title={ep.inbound_url || ''}
                    onClick={() => {
                      const url = ep.inbound_url;
                      if (url) {
                        navigator.clipboard.writeText(url).then(
                          () => showToast('Inbound URL copied!', 'success'),
                          () => window.prompt('Copy URL:', url),
                        );
                      }
                    }}
                  >
                    Copy URL
                  </button>
                </td>
                <td>
                  <span
                    className={
                      ep.endpoint_mode === 'trigger_update' ? css.badgeNeutral : css.badgeSuccess
                    }
                    style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}
                  >
                    {ep.endpoint_mode === 'trigger_update' ? 'Trigger/Update' : 'Form Input'}
                  </span>
                </td>
                <td>{instances.find((i) => i.id === ep.instance_id)?.name ?? '—'}</td>
                <td>{ep.signature_header}</td>
                <td>{ep.event_type_filter?.length ? ep.event_type_filter.join(', ') : 'All'}</td>
                <td>
                  <span className={ep.is_active ? css.badgeSuccess : css.badgeNeutral}>
                    {ep.is_active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => handleToggleEndpoint(ep)}
                    >
                      {ep.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={() => handleDeleteEndpoint(ep.id)}
                    >
                      Del
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Outbound Subscriptions ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
        }}
      >
        <h3 className={css.sectionSubtitle}>Outbound Subscriptions</h3>
        <button className={css.btnPrimary} onClick={() => setShowSubCreate(true)}>
          + New Subscription
        </button>
      </div>

      {subscriptions.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyMessage}>No outbound subscriptions</span>
        </div>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Target URL</th>
              <th>Events</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub: WebhookSubscription) => (
              <tr key={sub.id}>
                <td>{sub.name || '—'}</td>
                <td
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sub.target_url}
                </td>
                <td>{sub.event_types?.length ? sub.event_types.join(', ') : 'All'}</td>
                <td>
                  <span className={sub.is_active ? css.badgeSuccess : css.badgeNeutral}>
                    {sub.is_active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => handleToggleSubscription(sub)}
                    >
                      {sub.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={() => handleDeleteSubscription(sub.id)}
                    >
                      Del
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create endpoint modal */}
      {showEpCreate && (
        <div className={css.modalBackdrop} onClick={() => setShowEpCreate(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>New Inbound Endpoint</span>
              <button className={css.modalClose} onClick={() => setShowEpCreate(false)}>
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Endpoint Mode</label>
                <select
                  className={css.formSelect}
                  value={epForm.endpoint_mode}
                  onChange={(e) =>
                    setEpForm((f) => ({
                      ...f,
                      endpoint_mode: e.target.value as 'form_input' | 'trigger_update',
                    }))
                  }
                >
                  <option value="form_input">Form Input — create new contacts &amp; cases</option>
                  <option value="trigger_update">
                    Trigger / Update — match &amp; update existing records
                  </option>
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Connector Instance (optional)</label>
                <select
                  className={css.formSelect}
                  value={epForm.instance_id}
                  onChange={(e) =>
                    setEpForm((f) => ({ ...f, instance_id: Number(e.target.value) }))
                  }
                >
                  <option value={0}>None</option>
                  {instances.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>
                  Event Type Filter{' '}
                  <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>
                    (leave all unchecked to receive every event)
                  </span>
                </label>
                <EventTypeCheckboxes
                  selected={epForm.event_type_filter}
                  onChange={(v) => setEpForm((f) => ({ ...f, event_type_filter: v }))}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowEpCreate(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleCreateEndpoint}
                disabled={createEndpoint.isPending}
              >
                {createEndpoint.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create subscription modal */}
      {showSubCreate && (
        <div className={css.modalBackdrop} onClick={() => setShowSubCreate(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>New Outbound Subscription</span>
              <button className={css.modalClose} onClick={() => setShowSubCreate(false)}>
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Connector Instance (optional)</label>
                <select
                  className={css.formSelect}
                  value={subForm.instance_id}
                  onChange={(e) =>
                    setSubForm((f) => ({ ...f, instance_id: Number(e.target.value) }))
                  }
                >
                  <option value={0}>None</option>
                  {instances.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Name</label>
                <input
                  className={css.formInput}
                  placeholder="e.g. Slack Notifications"
                  value={subForm.name}
                  onChange={(e) => setSubForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Target URL</label>
                <input
                  className={css.formInput}
                  placeholder="https://example.com/webhook"
                  value={subForm.target_url}
                  onChange={(e) => setSubForm((f) => ({ ...f, target_url: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>
                  Event Types{' '}
                  <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>
                    (leave all unchecked to receive every event)
                  </span>
                </label>
                <EventTypeCheckboxes
                  selected={subForm.event_types}
                  onChange={(v) => setSubForm((f) => ({ ...f, event_types: v }))}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowSubCreate(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleCreateSubscription}
                disabled={createSubscription.isPending}
              >
                {createSubscription.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════════
 *  ETL MAPPING PANEL
 * ══════════════════════════════════════════ */

/** Recursively flatten a JSON object into dot-notation paths with example values */
function flattenPayload(obj: unknown, prefix = ''): Array<{ path: string; example: string }> {
  const result: Array<{ path: string; example: string }> = [];
  if (obj == null || typeof obj !== 'object') return result;
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result.push(...flattenPayload(value, path));
    } else {
      result.push({
        path,
        example: Array.isArray(value) ? JSON.stringify(value) : String(value ?? ''),
      });
    }
  }
  return result;
}

const TRANSFORMS = [
  { value: '', label: 'None' },
  { value: 'trim', label: 'Trim whitespace' },
  { value: 'lowercase', label: 'Lowercase' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'title_case', label: 'Title Case' },
  { value: 'date_iso', label: 'Date → ISO' },
  { value: 'to_array', label: 'Wrap in array' },
  { value: 'phone_e164', label: 'Phone E.164' },
];

interface DraftMapping {
  source_path: string;
  field_definition_id: number;
  transform: string;
  is_required: boolean;
  default_value: string;
  is_master: boolean;
}

interface DraftRecord {
  record_type: 'contact' | 'case';
  record_label: string;
  mappings: DraftMapping[];
}

function MappingPanel() {
  const { showToast } = useToast();
  const { data: endpoints = [], isLoading: epLoading } = useWebhookEndpoints();
  const { data: caseTypes = [] } = useLookupCaseTypes() as { data: CaseTypeLookup[] | undefined };
  const bulkSave = useBulkSaveMappings();
  const updateConfig = useUpdateProcessingConfig();
  const recapture = useRecaptureSample();

  const [selectedEpId, setSelectedEpId] = useState<number | null>(null);
  const selectedEp = endpoints.find((e) => e.id === selectedEpId) ?? null;

  const { data: existingMappings = [], isFetched: mappingsFetched } = useFieldMappings(
    selectedEpId,
  ) as { data: WebhookFieldMapping[] | undefined; isFetched: boolean };
  const { data: existingRecords = [], isFetched: recordsFetched } = useMappingRecords(
    selectedEpId,
  ) as { data: WebhookMappingRecord[] | undefined; isFetched: boolean };

  const LOG_PAGE_SIZE = 25;
  const [logPage, setLogPage] = useState(0);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const { data: logsData } = useProcessingLogs(selectedEpId, logPage, LOG_PAGE_SIZE);
  const processingLogs: WebhookProcessingLog[] = (logsData?.rows ?? []) as WebhookProcessingLog[];
  const logTotal = logsData?.total ?? 0;
  const logTotalPages = Math.ceil(logTotal / LOG_PAGE_SIZE);

  // Processing config state
  const [configCaseTypeId, setConfigCaseTypeId] = useState<number>(0);
  const [configStageId, setConfigStageId] = useState<number>(0);
  const [configLeadSource, setConfigLeadSource] = useState('Webhook');
  const [isProcessing, setIsProcessing] = useState(false);

  // Trigger/Update match config state
  const [matchField, setMatchField] = useState('');
  const [matchSourcePath, setMatchSourcePath] = useState('');
  const [matchTable, setMatchTable] = useState<'contacts' | 'cases'>('contacts');

  const { data: stages = [] } = useLookupStages(configCaseTypeId || null) as {
    data: StageLookup[] | undefined;
  };
  const { data: fieldDefinitions = [] } = useLookupFieldDefinitions() as {
    data: FieldDefinition[] | undefined;
  };

  // Draft records state
  const [draftRecords, setDraftRecords] = useState<DraftRecord[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Filter field defs by record type
  const contactFieldDefs = useMemo(
    () => fieldDefinitions.filter((fd) => fd.source_table === 'contacts'),
    [fieldDefinitions],
  );
  const caseFieldDefs = useMemo(
    () => fieldDefinitions.filter((fd) => fd.source_table !== 'contacts'),
    [fieldDefinitions],
  );

  // Sync existing data when endpoint is selected (wait for both queries to finish)
  if (selectedEp && !initialized && recordsFetched && mappingsFetched) {
    const pc = selectedEp.processing_config;
    if (pc) {
      if (pc.case_type_id && configCaseTypeId !== pc.case_type_id)
        setConfigCaseTypeId(pc.case_type_id);
      if (pc.stage_id && configStageId !== pc.stage_id) setConfigStageId(pc.stage_id as number);
      if (pc.lead_source) setConfigLeadSource(pc.lead_source as string);
      // Trigger/Update match fields
      if (pc.match_field) setMatchField(pc.match_field);
      if (pc.match_source_path) setMatchSourcePath(pc.match_source_path);
      if (pc.match_table) setMatchTable(pc.match_table);
    }
    setIsProcessing(!!selectedEp.is_processing);

    if (existingRecords.length > 0) {
      // Record-based: rebuild draft records from existing data
      const drafts: DraftRecord[] = existingRecords.map((rec: WebhookMappingRecord) => {
        const recMappings = existingMappings.filter(
          (m: WebhookFieldMapping) => m.record_id === rec.id,
        );
        return {
          record_type: rec.record_type,
          record_label: rec.record_label || '',
          mappings: recMappings.map((m: WebhookFieldMapping) => ({
            source_path: m.source_path,
            field_definition_id: m.field_definition_id,
            transform: m.transform || '',
            is_required: m.is_required,
            default_value: m.default_value || '',
            is_master: m.id === rec.master_field_id,
          })),
        };
      });
      setDraftRecords(drafts);
    } else if (existingMappings.length > 0) {
      // Legacy flat mappings — wrap into contact/case records
      const contactMappings = existingMappings.filter((m: WebhookFieldMapping) => {
        const fd = fieldDefinitions.find((f) => f.id === m.field_definition_id);
        return fd && fd.source_table === 'contacts';
      });
      const caseMappings = existingMappings.filter((m: WebhookFieldMapping) => {
        const fd = fieldDefinitions.find((f) => f.id === m.field_definition_id);
        return !fd || fd.source_table !== 'contacts';
      });
      const drafts: DraftRecord[] = [];
      if (contactMappings.length > 0) {
        drafts.push({
          record_type: 'contact',
          record_label: 'Contact',
          mappings: contactMappings.map((m: WebhookFieldMapping) => ({
            source_path: m.source_path,
            field_definition_id: m.field_definition_id,
            transform: m.transform || '',
            is_required: m.is_required,
            default_value: m.default_value || '',
            is_master: false,
          })),
        });
      }
      if (caseMappings.length > 0) {
        drafts.push({
          record_type: 'case',
          record_label: 'Case 1',
          mappings: caseMappings.map((m: WebhookFieldMapping) => ({
            source_path: m.source_path,
            field_definition_id: m.field_definition_id,
            transform: m.transform || '',
            is_required: m.is_required,
            default_value: m.default_value || '',
            is_master: false,
          })),
        });
      }
      setDraftRecords(drafts);
    }
    setInitialized(true);
  }

  // Flatten sample payload for field picker
  const sampleFields = useMemo(
    () => (selectedEp?.sample_payload ? flattenPayload(selectedEp.sample_payload) : []),
    [selectedEp?.sample_payload],
  );

  function handleSelectEndpoint(id: number) {
    setSelectedEpId(id);
    setDraftRecords([]);
    setConfigCaseTypeId(0);
    setConfigStageId(0);
    setConfigLeadSource('Webhook');
    setIsProcessing(false);
    setMatchField('');
    setMatchSourcePath('');
    setMatchTable('contacts');
    setInitialized(false);
    setLogPage(0);
  }

  function addRecord(type: 'contact' | 'case') {
    const caseCount = draftRecords.filter((r) => r.record_type === 'case').length;
    setDraftRecords((prev) => [
      ...prev,
      {
        record_type: type,
        record_label: type === 'contact' ? 'Contact' : `Case ${caseCount + 1}`,
        mappings: [],
      },
    ]);
  }

  function removeRecord(rIdx: number) {
    setDraftRecords((prev) => prev.filter((_, i) => i !== rIdx));
  }

  function updateRecordLabel(rIdx: number, label: string) {
    setDraftRecords((prev) => prev.map((r, i) => (i === rIdx ? { ...r, record_label: label } : r)));
  }

  function addMappingToRecord(rIdx: number) {
    setDraftRecords((prev) =>
      prev.map((r, i) =>
        i === rIdx
          ? {
              ...r,
              mappings: [
                ...r.mappings,
                {
                  source_path: '',
                  field_definition_id: 0,
                  transform: '',
                  is_required: false,
                  default_value: '',
                  is_master: false,
                },
              ],
            }
          : r,
      ),
    );
  }

  function removeMappingFromRecord(rIdx: number, mIdx: number) {
    setDraftRecords((prev) =>
      prev.map((r, i) =>
        i === rIdx ? { ...r, mappings: r.mappings.filter((_, mi) => mi !== mIdx) } : r,
      ),
    );
  }

  function updateMappingInRecord(
    rIdx: number,
    mIdx: number,
    field: keyof DraftMapping,
    value: string | boolean | number,
  ) {
    setDraftRecords((prev) =>
      prev.map((r, i) => {
        if (i !== rIdx) return r;
        const newMappings = r.mappings.map((m, mi) => {
          if (mi !== mIdx) {
            if (field === 'is_master' && value === true) return { ...m, is_master: false };
            return m;
          }
          if (field === 'field_definition_id') return { ...m, field_definition_id: Number(value) };
          return { ...m, [field]: value };
        });
        return { ...r, mappings: newMappings };
      }),
    );
  }

  // Quick-add from sample payload field into the first contact record
  function quickAdd(path: string) {
    let matchedFdId = 0;
    const lower = path.toLowerCase();
    for (const fd of fieldDefinitions) {
      const fdName = getFieldName(fd);
      const fdKey = getFieldKey(fd);
      if (lower.includes('first') && lower.includes('name') && fdName.includes('first name')) {
        matchedFdId = fd.id;
        break;
      }
      if (lower.includes('last') && lower.includes('name') && fdName.includes('last name')) {
        matchedFdId = fd.id;
        break;
      }
      if (lower.includes('email') && (fdName.includes('email') || fdKey.includes('email'))) {
        matchedFdId = fd.id;
        break;
      }
      if (
        (lower.includes('phone') || lower.includes('mobile')) &&
        (fdName.includes('phone') || fdKey.includes('phone'))
      ) {
        matchedFdId = fd.id;
        break;
      }
      if ((lower.includes('address') || lower.includes('street')) && fdName.includes('address')) {
        matchedFdId = fd.id;
        break;
      }
      if ((lower.includes('postcode') || lower.includes('zip')) && fdName.includes('postcode')) {
        matchedFdId = fd.id;
        break;
      }
      if ((lower.includes('dob') || lower.includes('birth')) && fdName.includes('date of birth')) {
        matchedFdId = fd.id;
        break;
      }
    }
    const contactIdx = draftRecords.findIndex((r) => r.record_type === 'contact');
    if (contactIdx >= 0) {
      setDraftRecords((prev) =>
        prev.map((r, i) => {
          if (i !== contactIdx) return r;
          return {
            ...r,
            mappings: [
              ...r.mappings,
              {
                source_path: path,
                field_definition_id: matchedFdId,
                transform: '',
                is_required: false,
                default_value: '',
                is_master: false,
              },
            ],
          };
        }),
      );
    } else {
      setDraftRecords((prev) => [
        ...prev,
        {
          record_type: 'contact',
          record_label: 'Contact',
          mappings: [
            {
              source_path: path,
              field_definition_id: matchedFdId,
              transform: '',
              is_required: false,
              default_value: '',
              is_master: false,
            },
          ],
        },
      ]);
    }
  }

  async function handleSaveMappings() {
    if (!selectedEpId) return;
    const validRecords = draftRecords.filter((r) =>
      r.mappings.some((m) => m.source_path && m.field_definition_id),
    );
    if (validRecords.length === 0) {
      showToast('Add at least one record with a complete mapping', 'error');
      return;
    }
    try {
      await bulkSave.mutateAsync({
        endpointId: selectedEpId,
        records: validRecords.map((r, ri) => {
          const validMappings = r.mappings.filter((m) => m.source_path && m.field_definition_id);
          const masterIdx = validMappings.findIndex((m) => m.is_master);
          return {
            record_type: r.record_type,
            record_label: r.record_label || null,
            sort_order: ri,
            master_field_index: masterIdx >= 0 ? masterIdx : null,
            mappings: validMappings.map((m, mi) => ({
              source_path: m.source_path,
              field_definition_id: m.field_definition_id,
              transform: m.transform || null,
              is_required: m.is_required,
              default_value: m.default_value || null,
              sort_order: mi,
            })),
          };
        }),
      });
      showToast(`${validRecords.length} record(s) saved`, 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to save mappings'), 'error');
    }
  }

  async function handleSaveConfig() {
    if (!selectedEpId || !configCaseTypeId) {
      showToast('Select a case type', 'error');
      return;
    }
    const isTriggerUpdate = selectedEp?.endpoint_mode === 'trigger_update';
    if (isTriggerUpdate && (!matchField || !matchSourcePath)) {
      showToast('Match Field and Match Source Path are required for Trigger/Update mode', 'error');
      return;
    }
    try {
      const configPayload: Record<string, unknown> = {
        case_type_id: configCaseTypeId,
        stage_id: configStageId || undefined,
        lead_source: configLeadSource || 'Webhook',
        create_contact: true,
        create_case: true,
      };
      if (isTriggerUpdate) {
        configPayload.match_field = matchField;
        configPayload.match_source_path = matchSourcePath;
        configPayload.match_table = matchTable;
      }
      await updateConfig.mutateAsync({
        endpointId: selectedEpId,
        processing_config: configPayload,
        is_processing: isProcessing,
      });
      showToast('Processing config saved', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to save config'), 'error');
    }
  }

  async function handleRecapture() {
    if (!selectedEpId) return;
    try {
      await recapture.mutateAsync(selectedEpId);
      showToast('Sample cleared — send a test webhook to recapture', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Failed to clear sample'), 'error');
    }
  }

  if (epLoading)
    return (
      <div className={css.emptyState}>
        <div className={css.spinner} />
      </div>
    );

  return (
    <>
      {/* Endpoint selector */}
      <div className={css.toolbar}>
        <div className={css.formGroup} style={{ margin: 0, minWidth: 300 }}>
          <select
            className={css.formSelect}
            value={selectedEpId ?? ''}
            onChange={(e) => handleSelectEndpoint(Number(e.target.value))}
          >
            <option value="">Select an endpoint to configure…</option>
            {endpoints.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.endpoint_key} [
                {ep.endpoint_mode === 'trigger_update' ? 'Trigger/Update' : 'Form Input'}]{' '}
                {ep.is_processing ? '(ETL Active)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedEp && (
        <div className={css.emptyState}>
          <span className={css.emptyIcon}>🔗</span>
          <span className={css.emptyMessage}>
            Select an inbound endpoint above to configure ETL field mapping
          </span>
        </div>
      )}

      {selectedEp && (
        <>
          {/* Processing Config */}
          <h3 className={css.sectionSubtitle}>Processing Config</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr auto',
              gap: 12,
              alignItems: 'end',
              marginBottom: selectedEp.endpoint_mode === 'trigger_update' ? 8 : 16,
            }}
          >
            <div className={css.formGroup} style={{ margin: 0 }}>
              <label className={css.formLabel}>Case Type</label>
              <select
                className={css.formSelect}
                value={configCaseTypeId}
                onChange={(e) => {
                  setConfigCaseTypeId(Number(e.target.value));
                  setConfigStageId(0);
                }}
              >
                <option value={0}>Select…</option>
                {(caseTypes ?? []).map((ct: CaseTypeLookup) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.display_name || ct.name}
                    {ct.ref_prefix ? ` (${ct.ref_prefix})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.formGroup} style={{ margin: 0 }}>
              <label className={css.formLabel}>Initial Stage</label>
              <select
                className={css.formSelect}
                value={configStageId}
                onChange={(e) => setConfigStageId(Number(e.target.value))}
              >
                <option value={0}>Default (first stage)</option>
                {(stages ?? []).map((s: StageLookup) => (
                  <option key={s.stage_id} value={s.stage_id}>
                    {s.stage_name}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.formGroup} style={{ margin: 0 }}>
              <label className={css.formLabel}>Lead Source</label>
              <input
                className={css.formInput}
                value={configLeadSource}
                onChange={(e) => setConfigLeadSource(e.target.value)}
                placeholder="Webhook"
              />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isProcessing}
                  onChange={(e) => setIsProcessing(e.target.checked)}
                />
                Live
              </label>
              <button
                className={css.btnPrimary}
                onClick={handleSaveConfig}
                disabled={updateConfig.isPending}
              >
                {updateConfig.isPending ? 'Saving…' : 'Save Config'}
              </button>
            </div>
          </div>

          {/* Trigger/Update Match Config */}
          {selectedEp.endpoint_mode === 'trigger_update' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12,
                alignItems: 'end',
                marginBottom: 16,
                padding: 12,
                background: 'rgba(255,193,7,0.06)',
                borderRadius: 6,
                border: '1px solid rgba(255,193,7,0.25)',
              }}
            >
              <div className={css.formGroup} style={{ margin: 0 }}>
                <label className={css.formLabel}>Match Table</label>
                <select
                  className={css.formSelect}
                  value={matchTable}
                  onChange={(e) => setMatchTable(e.target.value as 'contacts' | 'cases')}
                >
                  <option value="contacts">Contacts</option>
                  <option value="cases">Cases</option>
                </select>
              </div>
              <div className={css.formGroup} style={{ margin: 0 }}>
                <label className={css.formLabel}>Match DB Column</label>
                <input
                  className={css.formInput}
                  value={matchField}
                  onChange={(e) => setMatchField(e.target.value)}
                  placeholder="e.g. ContactID, Email, CaseRef"
                />
              </div>
              <div className={css.formGroup} style={{ margin: 0 }}>
                <label className={css.formLabel}>Match Source Path (in payload)</label>
                <input
                  list="match-src-paths"
                  className={css.formInput}
                  value={matchSourcePath}
                  onChange={(e) => setMatchSourcePath(e.target.value)}
                  placeholder="e.g. data.contact_id or 5.1"
                />
                <datalist id="match-src-paths">
                  {sampleFields.map((f) => (
                    <option key={f.path} value={f.path} />
                  ))}
                </datalist>
              </div>
            </div>
          )}

          {/* Sample Payload */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className={css.sectionSubtitle}>Sample Payload Fields</h3>
            <button
              className={`${css.btnSecondary} ${css.btnSmall}`}
              onClick={handleRecapture}
              disabled={recapture.isPending}
            >
              {recapture.isPending ? 'Clearing…' : 'Recapture Sample'}
            </button>
          </div>

          {sampleFields.length === 0 ? (
            <div className={css.emptyState} style={{ padding: '16px 0' }}>
              <span className={css.emptyMessage}>
                No sample payload captured yet — send a test webhook to this endpoint first
              </span>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 16,
                padding: 12,
                background: 'var(--color-surface, #1a1a2e)',
                borderRadius: 6,
                border: '1px solid var(--color-border, #333)',
              }}
            >
              {sampleFields.map((f) => (
                <button
                  key={f.path}
                  className={`${css.btnSecondary} ${css.btnSmall}`}
                  onClick={() => quickAdd(f.path)}
                  title={`Example: ${f.example}`}
                  style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {f.path} <span style={{ opacity: 0.5, marginLeft: 4 }}>=</span>{' '}
                  <span
                    style={{
                      opacity: 0.6,
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                      verticalAlign: 'bottom',
                    }}
                  >
                    {f.example.substring(0, 30)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Record Groups */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className={css.sectionSubtitle}>Record Mappings</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {!draftRecords.some((r) => r.record_type === 'contact') && (
                <button
                  className={`${css.btnSecondary} ${css.btnSmall}`}
                  onClick={() => addRecord('contact')}
                >
                  + Contact
                </button>
              )}
              <button
                className={`${css.btnSecondary} ${css.btnSmall}`}
                onClick={() => addRecord('case')}
              >
                + Case
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleSaveMappings}
                disabled={bulkSave.isPending}
              >
                {bulkSave.isPending ? 'Saving…' : 'Save All Mappings'}
              </button>
            </div>
          </div>

          {draftRecords.length === 0 ? (
            <div className={css.emptyState} style={{ padding: '16px 0' }}>
              <span className={css.emptyMessage}>
                No records yet — add a Contact or Case record to start mapping fields
              </span>
            </div>
          ) : (
            draftRecords.map((rec, rIdx) => {
              const isContact = rec.record_type === 'contact';
              const availableFields = isContact ? contactFieldDefs : caseFieldDefs;
              return (
                <div
                  key={rIdx}
                  style={{
                    marginBottom: 20,
                    border: `1px solid ${isContact ? 'var(--color-primary, #4a6cf7)' : 'var(--color-border, #333)'}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  {/* Record header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      background: isContact
                        ? 'rgba(74,108,247,0.1)'
                        : 'var(--color-surface, #1a1a2e)',
                    }}
                  >
                    <span
                      className={isContact ? css.badgeSuccess : css.badgeNeutral}
                      style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}
                    >
                      {rec.record_type}
                    </span>
                    <input
                      className={css.formInput}
                      value={rec.record_label}
                      onChange={(e) => updateRecordLabel(rIdx, e.target.value)}
                      placeholder={isContact ? 'Contact' : 'Case label…'}
                      style={{ flex: 1, fontSize: '0.85rem', padding: '4px 8px' }}
                    />
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => addMappingToRecord(rIdx)}
                    >
                      + Field
                    </button>
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={() => removeRecord(rIdx)}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Mappings table */}
                  {rec.mappings.length === 0 ? (
                    <div style={{ padding: '12px 16px', opacity: 0.5, fontSize: '0.85rem' }}>
                      No fields mapped — click "+ Field" to add
                    </div>
                  ) : (
                    <table className={css.table} style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th>Source Path</th>
                          <th>Target Field</th>
                          <th>Transform</th>
                          <th>Req</th>
                          <th>Default</th>
                          <th title="Master field — if empty, this record is skipped">★</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rec.mappings.map((m, mIdx) => (
                          <tr key={mIdx}>
                            <td>
                              <input
                                list={`src-paths-${rIdx}-${mIdx}`}
                                className={css.formInput}
                                value={m.source_path}
                                onChange={(e) =>
                                  updateMappingInRecord(rIdx, mIdx, 'source_path', e.target.value)
                                }
                                placeholder="e.g. 1.3 or {1.3} {1.6}"
                                style={{
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  minWidth: 160,
                                }}
                              />
                              <datalist id={`src-paths-${rIdx}-${mIdx}`}>
                                {sampleFields.map((f) => (
                                  <option key={f.path} value={f.path} />
                                ))}
                              </datalist>
                            </td>
                            <td>
                              <select
                                className={css.formSelect}
                                value={m.field_definition_id || ''}
                                onChange={(e) =>
                                  updateMappingInRecord(
                                    rIdx,
                                    mIdx,
                                    'field_definition_id',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select…</option>
                                {availableFields.map((fd) => (
                                  <option key={fd.id} value={fd.id}>
                                    {getFieldLabel(fd)}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className={css.formSelect}
                                value={m.transform}
                                onChange={(e) =>
                                  updateMappingInRecord(rIdx, mIdx, 'transform', e.target.value)
                                }
                              >
                                {TRANSFORMS.map((t) => (
                                  <option key={t.value} value={t.value}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={m.is_required}
                                onChange={(e) =>
                                  updateMappingInRecord(rIdx, mIdx, 'is_required', e.target.checked)
                                }
                              />
                            </td>
                            <td>
                              <input
                                className={css.formInput}
                                value={m.default_value}
                                onChange={(e) =>
                                  updateMappingInRecord(rIdx, mIdx, 'default_value', e.target.value)
                                }
                                placeholder="—"
                                style={{ fontSize: '0.8rem', width: 80 }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="radio"
                                name={`master-${rIdx}`}
                                checked={m.is_master}
                                onChange={() =>
                                  updateMappingInRecord(rIdx, mIdx, 'is_master', true)
                                }
                                title="Set as master field (gates record creation)"
                              />
                            </td>
                            <td>
                              <button
                                className={`${css.btnDanger} ${css.btnSmall}`}
                                onClick={() => removeMappingFromRecord(rIdx, mIdx)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}

          {/* Processing Log */}
          <h3 className={css.sectionSubtitle} style={{ marginTop: 24 }}>
            Processing Log{' '}
            {logTotal > 0 && (
              <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.6 }}>
                ({logTotal} entries)
              </span>
            )}
          </h3>
          {processingLogs.length === 0 ? (
            <div className={css.emptyState} style={{ padding: '16px 0' }}>
              <span className={css.emptyMessage}>No processing records yet</span>
            </div>
          ) : (
            <>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Contact ID</th>
                    <th>Case Ref</th>
                    <th>Error</th>
                    <th>Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {processingLogs.map((l: WebhookProcessingLog) => {
                    const notes: ProcessingFieldNote[] = l.field_notes ?? [];
                    const filledCount = notes.filter((n) => n.status === 'filled').length;
                    const skippedCount = notes.filter((n) => n.status === 'skipped').length;
                    const errorCount = notes.filter((n) => n.status === 'error').length;
                    const isExpanded = expandedLogId === l.id;
                    return (
                      <Fragment key={l.id}>
                        <tr>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(l.created_at).toLocaleString()}
                          </td>
                          <td>
                            <span
                              className={
                                l.status === 'success'
                                  ? css.badgeSuccess
                                  : l.status === 'error'
                                    ? css.badgeError
                                    : css.badgeNeutral
                              }
                            >
                              {l.status}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {l.contact_id || '—'}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {l.case_ref || '—'}
                          </td>
                          <td
                            style={{
                              color: 'var(--color-danger, #f44)',
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {l.error_message || '—'}
                          </td>
                          <td>
                            {notes.length > 0 ? (
                              <button
                                className={`${css.btnSecondary} ${css.btnSmall}`}
                                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                onClick={() => setExpandedLogId(isExpanded ? null : l.id)}
                              >
                                {filledCount}✓ {skippedCount > 0 ? `${skippedCount}⊘ ` : ''}
                                {errorCount > 0 ? `${errorCount}✗ ` : ''}
                                {isExpanded ? '▲' : '▼'}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                        {isExpanded && notes.length > 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              style={{
                                padding: '8px 12px',
                                background: 'var(--color-surface-alt, rgba(255,255,255,0.03))',
                              }}
                            >
                              <table
                                style={{
                                  width: '100%',
                                  fontSize: '0.8rem',
                                  borderCollapse: 'collapse',
                                }}
                              >
                                <thead>
                                  <tr style={{ opacity: 0.6 }}>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>
                                      Record
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>Field</th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>
                                      Source Path
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>
                                      Status
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>
                                      Reason
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>Value</th>
                                    <th style={{ textAlign: 'left', padding: '2px 8px' }}>
                                      Target Column
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {notes.map((n, idx) => (
                                    <tr
                                      key={idx}
                                      style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
                                    >
                                      <td style={{ padding: '2px 8px' }}>{n.record || '—'}</td>
                                      <td style={{ padding: '2px 8px' }}>{n.field}</td>
                                      <td
                                        style={{
                                          padding: '2px 8px',
                                          fontFamily: 'monospace',
                                          fontSize: '0.75rem',
                                          opacity: 0.7,
                                          maxWidth: 160,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {n.source_path || '—'}
                                      </td>
                                      <td style={{ padding: '2px 8px' }}>
                                        <span
                                          style={{
                                            color:
                                              n.status === 'filled'
                                                ? 'var(--color-success, #4f4)'
                                                : n.status === 'error'
                                                  ? 'var(--color-danger, #f44)'
                                                  : 'var(--color-warning, #fa0)',
                                          }}
                                        >
                                          {n.status}
                                        </span>
                                      </td>
                                      <td style={{ padding: '2px 8px', opacity: 0.7 }}>
                                        {n.reason || '—'}
                                      </td>
                                      <td
                                        style={{
                                          padding: '2px 8px',
                                          fontFamily: 'monospace',
                                          maxWidth: 200,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {n.value ?? n.source_value ?? '—'}
                                      </td>
                                      <td style={{ padding: '2px 8px', fontFamily: 'monospace' }}>
                                        {n.target || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {logTotalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    padding: '12px 0',
                  }}
                >
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => setLogPage((p) => Math.max(0, p - 1))}
                    disabled={logPage === 0}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: '0.85rem' }}>
                    Page {logPage + 1} of {logTotalPages}
                  </span>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => setLogPage((p) => Math.min(logTotalPages - 1, p + 1))}
                    disabled={logPage >= logTotalPages - 1}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

/* ══════════════════════════════════════════
 *  DELIVERIES PANEL
 * ══════════════════════════════════════════ */

function DeliveriesPanel() {
  const { showToast } = useToast();
  const [filterSubId, setFilterSubId] = useState<number | undefined>(undefined);
  const { data: deliveries = [], isLoading } = useWebhookDeliveries(filterSubId);
  const { data: subscriptions = [] } = useWebhookSubscriptions();
  const retryDelivery = useRetryDelivery();

  async function handleRetry(id: number) {
    try {
      await retryDelivery.mutateAsync(id);
      showToast('Delivery retried', 'success');
    } catch (e: unknown) {
      showToast(getApiErrorMessage(e, 'Retry failed'), 'error');
    }
  }

  const statusBadge = (s: WebhookDelivery['status']) => {
    switch (s) {
      case 'delivered':
        return css.badgeSuccess;
      case 'pending':
        return css.badgeInfo;
      case 'failed':
        return css.badgeWarning;
      case 'dead_letter':
        return css.badgeError;
      default:
        return css.badgeNeutral;
    }
  };

  if (isLoading)
    return (
      <div className={css.emptyState}>
        <div className={css.spinner} />
      </div>
    );

  return (
    <>
      <div className={css.toolbar}>
        <div className={css.formGroup} style={{ margin: 0 }}>
          <select
            className={css.formSelect}
            value={filterSubId ?? ''}
            onChange={(e) => setFilterSubId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">All subscriptions</option>
            {subscriptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Sub #${s.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {deliveries.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyIcon}>📬</span>
          <span className={css.emptyMessage}>No deliveries yet</span>
        </div>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Event</th>
              <th>Status</th>
              <th>HTTP</th>
              <th>Attempts</th>
              <th>Next Retry</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map((d: WebhookDelivery) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td>{d.event_type}</td>
                <td>
                  <span className={statusBadge(d.status)}>{d.status}</span>
                </td>
                <td>{d.http_status ?? '—'}</td>
                <td>
                  {d.attempt_count}/{d.max_attempts}
                </td>
                <td>{d.next_retry_at ? new Date(d.next_retry_at).toLocaleString() : '—'}</td>
                <td>{new Date(d.created_at).toLocaleString()}</td>
                <td>
                  {(d.status === 'failed' || d.status === 'dead_letter') && (
                    <button
                      className={css.btnSmall}
                      onClick={() => handleRetry(d.id)}
                      disabled={retryDelivery.isPending}
                    >
                      ↻ Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/* ══════════════════════════════════════════
 *  AUDIT LOG PANEL
 * ══════════════════════════════════════════ */

function AuditPanel() {
  const [entityFilter, setEntityFilter] = useState('');
  const [expandedLog, setExpandedLog] = useState<IntegrationAuditEntry | null>(null);
  const ROWS_PER_PAGE = 25;
  const [auditPage, setAuditPage] = useState(1);
  const { data: logs = [], isLoading } = useIntegrationAuditLogs(
    entityFilter ? { entity_type: entityFilter, limit: 200 } : { limit: 200 },
  );

  if (isLoading)
    return (
      <div className={css.emptyState}>
        <div className={css.spinner} />
      </div>
    );

  const entityTypes = [...new Set(logs.map((l: IntegrationAuditEntry) => l.entity_type))];
  const auditTotalPages = Math.ceil(logs.length / ROWS_PER_PAGE);
  const pagedLogs = logs.slice((auditPage - 1) * ROWS_PER_PAGE, auditPage * ROWS_PER_PAGE);

  return (
    <>
      <div className={css.toolbar}>
        <div className={css.formGroup} style={{ margin: 0 }}>
          <select
            className={css.formSelect}
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
          >
            <option value="">All entity types</option>
            {entityTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.emptyIcon}>📋</span>
          <span className={css.emptyMessage}>No audit entries</span>
        </div>
      ) : (
        <>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Entity ID</th>
                <th>Actor</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {pagedLogs.map((l: IntegrationAuditEntry) => (
                <tr key={l.id} onClick={() => setExpandedLog(l)} style={{ cursor: 'pointer' }}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td>
                    <span className={css.badgeInfo}>{l.action}</span>
                  </td>
                  <td>{l.entity_type}</td>
                  <td>{l.entity_id ?? '—'}</td>
                  <td>{l.actor_user_id ?? 'system'}</td>
                  <td
                    style={{
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {JSON.stringify(l.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditTotalPages > 1 && (
            <div className={css.pagination}>
              <button
                className={css.pageBtn}
                disabled={auditPage === 1}
                onClick={() => setAuditPage(auditPage - 1)}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                Page {auditPage} of {auditTotalPages} ({logs.length} total)
              </span>
              <button
                className={css.pageBtn}
                disabled={auditPage >= auditTotalPages}
                onClick={() => setAuditPage(auditPage + 1)}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}

      {expandedLog && (
        <div className={css.modalBackdrop} onClick={() => setExpandedLog(null)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className={css.modalHeader}>
              <span className={css.modalTitle}>Audit Entry — {expandedLog.action}</span>
              <button className={css.modalClose} onClick={() => setExpandedLog(null)}>
                ✕
              </button>
            </div>
            <div className={css.modalBody}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: '8px 12px',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 16,
                }}
              >
                <span style={{ fontWeight: 600 }}>Time</span>
                <span>{new Date(expandedLog.created_at).toLocaleString()}</span>
                <span style={{ fontWeight: 600 }}>Action</span>
                <span>{expandedLog.action}</span>
                <span style={{ fontWeight: 600 }}>Entity Type</span>
                <span>{expandedLog.entity_type}</span>
                <span style={{ fontWeight: 600 }}>Entity ID</span>
                <span>{expandedLog.entity_id ?? '—'}</span>
                <span style={{ fontWeight: 600 }}>Actor</span>
                <span>{expandedLog.actor_user_id ?? 'system'}</span>
              </div>
              <div>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Details</span>
                <pre
                  style={{
                    background: 'var(--color-surface, #1a1a2e)',
                    border: '1px solid var(--color-border, #333)',
                    borderRadius: 6,
                    padding: 12,
                    marginTop: 6,
                    fontSize: 'var(--font-size-xs)',
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(expandedLog.details, null, 2)}
                </pre>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setExpandedLog(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
